import { TablePaginationConfig } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { SetURLSearchParams } from 'react-router-dom';
import { UseTableReturn } from 'type/tableType';

interface UseTableProps<T> {
  dataSource: T[];
  setSelectedList: React.Dispatch<React.SetStateAction<number[]>>;
  setSearchParams: SetURLSearchParams;
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTable = <T>({
  dataSource,
  setSearchParams,
  setSelectAll,
  setSelectedList,
}: UseTableProps<T>): UseTableReturn<T> => {
  const onPageChange = async (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ): Promise<void> => {
    const { current, pageSize } = pagination;
    const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;

    if (field) {
      setSearchParams(prev => {
        prev.set('order_by', field.toString() || 'id');
        return prev;
      });
    } else {
      setSearchParams(prev => {
        prev.delete('order_by');
        return prev;
      });
    }

    if (order) {
      setSearchParams(prev => {
        prev.set('order_direction', order === 'ascend' ? 'asc' : 'desc');
        return prev;
      });
    } else {
      setSearchParams(prev => {
        prev.delete('order_direction');
        return prev;
      });
    }

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          setSearchParams(prev => {
            prev.set(key, value?.toString() || '');
            return prev;
          });
        } else {
          setSearchParams(prev => {
            prev.delete(key);
            return prev;
          });
        }
      });
    }

    setSearchParams(prev => {
      prev.set('page', current?.toString() || '1');
      prev.set('page_size', pageSize?.toString() || '10');
      return prev;
    });
  };

  const handleSelectAll = (e: CheckboxChangeEvent): void => {
    const isChecked = e.target.checked;
    if (isChecked) {
      setSelectedList(dataSource.map(item => (item as { id: number }).id));
      setSelectAll(true);
    } else {
      setSelectedList([]);
      setSelectAll(false);
    }
  };

  const handleSelect = (id: number): void => {
    setSelectedList(prevSelectedList =>
      prevSelectedList.includes(id)
        ? prevSelectedList.filter(existingId => existingId !== id)
        : [...prevSelectedList, id]
    );
  };

  return {
    onPageChange,
    handleSelectAll,
    handleSelect,
  };
};
